import { defaultMyGoalsBoardFilters } from '@helpers/constants';
import { isExpired } from '@helpers/isExpired';
import { demoEntry, demoUser, reportStorageUrl } from '@http/constants';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import type {
  AccessibilityData,
  AuditData,
  Responsibility,
  WebsiteData,
  TranslateData,
  WcagCheckerData,
  UserData,
  TeamEffectiveRightsByProductType,
} from '@eye-able/types';
import type { GraphTimeRange } from './pages/report/reportComponents/charts/types';
import type { JiraConfig } from './http/queries/useFetchJiraConfig';
import type { TodoNotification, TodosData } from './http/queries/useFetchTodos';

// UI
export const activeNavItemAtom = atom('');

let isDemo = false;

if (window.location.pathname === '/demo') {
  window.localStorage.setItem('demoMode', 'true');
  isDemo = true;
} else {
  window.localStorage.setItem('demoMode', 'false');
}

// Demo Mode for the whole dashboard, has the demo user logged in
export const demoModeAtom = atomWithStorage('demoMode', isDemo);

// The organisation the user is currently acting in as a guest (null = own organisation).
// Persisted under the same key the fetchWrapper reads to send the X-Guest-Organisation header.
export const selectedGuestOrgAtom = atomWithStorage<number | null>(
  'X-Guest-Organisation',
  null,
);

interface StorageValues {
  accordionExpanded: Record<string, unknown>;
  /** App-wide preferred language for AI-explanation texts; null = each finding's original. */
  aiExplanationLanguage: string | null;
  checkerLicenses: unknown[];
  cookieBannerAccepted: boolean;
  currentAuditEntryId: number | null;
  allyPromptBannerDismissed: boolean;
  glossaryNewsSeenV1: boolean;
  myGoalsBoardFilters: typeof defaultMyGoalsBoardFilters;
  notificationLastViewDate: number;
  reactTableFilters: Record<string, unknown>;
  reactTableSorting: Record<string, unknown>;
  rerenderLayout: string;
  selectedAssistDomain: string;
  selectedGraphTimeRange: GraphTimeRange;
  spellCheckSitesColumnFilters: unknown[];
  spellCheckSitesColumnSorting: Record<string, unknown>[];
  tokenExpires: number;
  userToursSeen: unknown[];
  versionOfLastNotificationSeen: string;
  websiteLicenses: unknown[];
}

const sessionStorageKeys = new Set<keyof StorageValues>([
  'currentAuditEntryId',
]);

function getStorage(key: keyof StorageValues): Storage {
  return sessionStorageKeys.has(key)
    ? window.sessionStorage
    : window.localStorage;
}

function getItem(key: keyof StorageValues): string | null {
  return getStorage(key).getItem(key);
}

const rawStorage: Record<keyof StorageValues, string | null> = {
  accordionExpanded: getItem('accordionExpanded'),
  aiExplanationLanguage: getItem('aiExplanationLanguage'),
  checkerLicenses: getItem('checkerLicenses'),
  cookieBannerAccepted: getItem('cookieBannerAccepted'),
  currentAuditEntryId: getItem('currentAuditEntryId'),
  allyPromptBannerDismissed: getItem('allyPromptBannerDismissed'),
  glossaryNewsSeenV1: getItem('glossaryNewsSeenV1'),
  myGoalsBoardFilters: getItem('myGoalsBoardFilters'),
  notificationLastViewDate: getItem('notificationLastViewDate'),
  reactTableFilters: getItem('reactTableFilters'),
  reactTableSorting: getItem('reactTableSorting'),
  rerenderLayout: getItem('rerenderLayout'),
  selectedAssistDomain: getItem('selectedAssistDomain'),
  selectedGraphTimeRange: getItem('selectedGraphTimeRange'),
  spellCheckSitesColumnFilters: getItem('spellCheckSitesColumnFilters'),
  spellCheckSitesColumnSorting: getItem('spellCheckSitesColumnSorting'),
  tokenExpires: getItem('tokenExpires'),
  userToursSeen: getItem('userToursSeen'),
  versionOfLastNotificationSeen: getItem('versionOfLastNotificationSeen'),
  websiteLicenses: getItem('websiteLicenses'),
};

function clone<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value)) as T;
}

let isCorruptionHandled = false;

function handleCorruptedStorage(
  error: unknown,
  key: keyof StorageValues,
): void {
  if (isCorruptionHandled) return;

  isCorruptionHandled = true;
  console.error('Invalid JSON in store!', error, key);

  window.localStorage.clear();
  window.sessionStorage.clear();
  window.location.replace('/authentication/login');
}

function parse<T>(
  key: keyof StorageValues,
  value: string | null,
  fallback: () => T,
): T {
  if (value === null) return fallback();

  try {
    return JSON.parse(value) as T;
  } catch (error) {
    handleCorruptedStorage(error, key);
    return fallback();
  }
}

// Note: Fallbacks as functions defer execution until needed and ensure
// composite defaults are not shared references.
const localValues: StorageValues = {
  rerenderLayout: parse('rerenderLayout', rawStorage.rerenderLayout, () =>
    Date.now().toString(),
  ),
  currentAuditEntryId: parse(
    'currentAuditEntryId',
    rawStorage.currentAuditEntryId,
    () => null,
  ),
  tokenExpires: parse('tokenExpires', rawStorage.tokenExpires, () => 0),
  websiteLicenses: parse(
    'websiteLicenses',
    rawStorage.websiteLicenses,
    () => [],
  ),
  checkerLicenses: parse(
    'checkerLicenses',
    rawStorage.checkerLicenses,
    () => [],
  ),
  selectedAssistDomain: parse(
    'selectedAssistDomain',
    rawStorage.selectedAssistDomain,
    () => '',
  ),
  selectedGraphTimeRange: parse(
    'selectedGraphTimeRange',
    rawStorage.selectedGraphTimeRange,
    () => 'threeMonths',
  ),
  cookieBannerAccepted: parse(
    'cookieBannerAccepted',
    rawStorage.cookieBannerAccepted,
    () => false,
  ),
  allyPromptBannerDismissed: parse(
    'allyPromptBannerDismissed',
    rawStorage.allyPromptBannerDismissed,
    () => false,
  ),
  glossaryNewsSeenV1: parse(
    'glossaryNewsSeenV1',
    rawStorage.glossaryNewsSeenV1,
    () => false,
  ),
  versionOfLastNotificationSeen: parse(
    'versionOfLastNotificationSeen',
    rawStorage.versionOfLastNotificationSeen,
    () => '1.0.0',
  ),
  notificationLastViewDate: parse(
    'notificationLastViewDate',
    rawStorage.notificationLastViewDate,
    () => 0,
  ),
  userToursSeen: parse('userToursSeen', rawStorage.userToursSeen, () => []),
  myGoalsBoardFilters: parse(
    'myGoalsBoardFilters',
    rawStorage.myGoalsBoardFilters,
    () => clone(defaultMyGoalsBoardFilters),
  ),
  reactTableSorting: parse(
    'reactTableSorting',
    rawStorage.reactTableSorting,
    () => ({}),
  ),
  reactTableFilters: parse(
    'reactTableFilters',
    rawStorage.reactTableFilters,
    () => ({}),
  ),
  accordionExpanded: parse(
    'accordionExpanded',
    rawStorage.accordionExpanded,
    () => ({}),
  ),
  aiExplanationLanguage: parse(
    'aiExplanationLanguage',
    rawStorage.aiExplanationLanguage,
    () => null,
  ),
  spellCheckSitesColumnSorting: parse(
    'spellCheckSitesColumnSorting',
    rawStorage.spellCheckSitesColumnSorting,
    () => [],
  ),
  spellCheckSitesColumnFilters: parse(
    'spellCheckSitesColumnFilters',
    rawStorage.spellCheckSitesColumnFilters,
    () => [],
  ),
};

export const tokenExpiresAtom = atomWithStorage(
  'tokenExpires',
  localValues.tokenExpires,
);

// Demo Modes for specific products are derived from the license atoms below:
// true when the user is logged in but has no license for the product, and
// always true in general demo mode (where no licenses are fetched at all).

// Current User
export const userAtom = atom<UserData | typeof demoUser | undefined>(
  isDemo ? demoUser : undefined,
);
// real current user (fully typed), not the demo user
export const userDataAtom = atom<UserData | undefined>((get) => {
  const user = get(userAtom);
  if (!user) return undefined;
  if (user === demoUser) return undefined;
  return user as UserData;
});

export const assistDomainAtom = atomWithStorage(
  'selectedAssistDomain',
  localValues.selectedAssistDomain,
);

export const effectiveAccessRightsAtom =
  atom<TeamEffectiveRightsByProductType | null>(null);
export const auditLicensesAtom = atom<WcagCheckerData[]>([]);
// derived: Audit falls back to demo mode whenever the org has no audit license
// (which is also the case in general demo mode, where no licenses are fetched)
export const auditDemoModeAtom = atom(
  (get) => get(demoModeAtom) || !get(auditLicensesAtom).length,
);
export const assistLicensesAtom = atom<WebsiteData[]>([]);

export const assistDemoModeAtom = atom(
  (get) =>
    get(demoModeAtom) ||
    !get(assistLicensesAtom).some(
      (license) => license.endDate !== null && !isExpired(license.endDate),
    ),
);

export const demoAuditEntry = demoEntry as unknown as AuditData;

export const availableAuditEntriesAtom = atom<AuditData[]>([]);

export const reportDemoModeAtom = atom(
  (get) =>
    get(demoModeAtom) ||
    get(availableAuditEntriesAtom).some((entry) => entry === demoAuditEntry),
);

export const accStatementDemoModeAtom = atom(
  (get) =>
    get(demoModeAtom) ||
    !get(availableAuditEntriesAtom).some((entry) => entry.statementGenerator),
);
export const translateEasyLicenses = atom<TranslateData[]>([]);

// each product has its own character budget on the shared translate license
export const translateDemoModeAtom = atom(
  (get) =>
    get(demoModeAtom) ||
    !get(translateEasyLicenses).some((license) => license.charLimit > 0),
);

export const easyDemoModeAtom = atom(
  (get) =>
    get(demoModeAtom) ||
    !get(translateEasyLicenses).some((license) => license.charLimitEasy > 0),
);
export const issueContentLicensesAtom = atom<Responsibility[]>([]);
export const orgUsersAtom = atom([]);
export const todosDataAtom = atom<TodosData>({ todos: {} });
export const jiraConfigAtom = atom<JiraConfig | null>(null);
export const userNotificationsAtom = atom<TodoNotification[]>([]);
export const accessibilityDataAtom = atom<AccessibilityData | null>(null);
export const primaryReportDateAtom = atom<Date | null>(null);
export const secondaryReportDateAtom = atom<Date | null>(null);

export type TransSelectedDomain = {
  id: number | string;
  domain: string;
  usedChars?: number | string | null;
  charLimit?: number | string | null;
  usedCharsEasy?: number | string | null;
  charLimitEasy?: number | string | null;
  usage?: string;
  created?: string;
};

export const transSelectedDomainAtom = atomWithStorage<
  TransSelectedDomain | ''
>('transSelectedDomain', '');

export const sharedConfiguratorDomainAtom = atomWithStorage(
  'sharedConfiguratorDomain',
  '',
);

// Active selection lives per-tab in sessionStorage (tabs stay independent),
// mirrored to localStorage under `lastAuditEntryId` so a fresh tab (open in new
// tab, bookmark, restart) inherits the last domain instead of the first entry.
const LAST_AUDIT_ENTRY_KEY = 'lastAuditEntryId';

const auditEntryStorage = createJSONStorage<number | null>(() => ({
  getItem: (key) => {
    const active = window.sessionStorage.getItem(key);
    if (active !== null) {
      // Sync the seed with the active selection even when it was set passively
      // (URL, bootstrap default, pre-existing value) and setItem never ran.
      window.localStorage.setItem(LAST_AUDIT_ENTRY_KEY, active);
      return active;
    }

    return window.localStorage.getItem(LAST_AUDIT_ENTRY_KEY);
  },
  setItem: (key, value) => {
    window.sessionStorage.setItem(key, value);
    window.localStorage.setItem(LAST_AUDIT_ENTRY_KEY, value);
  },
  removeItem: (key) => {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(LAST_AUDIT_ENTRY_KEY);
  },
}));

export const currentAuditEntryIdAtom = atomWithStorage<number | null>(
  'currentAuditEntryId',
  localValues.currentAuditEntryId,
  auditEntryStorage,

  // Without this the atom starts as null and only reads storage on mount, so
  // the bootstrap would see null first and reset to the first entry. getOnInit
  // reads storage at creation, so the persisted id is there from render one.
  { getOnInit: true },
);

export const currentAuditEntryAtom = atom((get) => {
  const currentAuditEntryId = get(currentAuditEntryIdAtom);
  const availableAuditEntries = get(availableAuditEntriesAtom);

  if (!currentAuditEntryId || !availableAuditEntries?.length) return null;

  return (
    availableAuditEntries.find((entry) => {
      return entry.id === currentAuditEntryId;
    }) ?? null
  );
});

export const selectedDomainAtom = atom((get) => {
  const entry = get(currentAuditEntryAtom);

  if (!entry) return null;

  return entry.domain;
});

export const currentCdnDirectoryAtom = atom((get) => {
  const currentAuditEntry = get(currentAuditEntryAtom);

  if (!currentAuditEntry) return null;

  return currentAuditEntry.cdnDirectory;
});

export const fullCdnPathAtom = atom((get) => {
  const currentCdnDirectory = get(currentCdnDirectoryAtom);
  if (!currentCdnDirectory) {
    return null;
  }

  return (
    reportStorageUrl +
    currentCdnDirectory +
    (currentCdnDirectory.endsWith('/') ? '' : '/')
  );
});

export const fullUsersCdnPathAtom = atom((get) => {
  const user = get(userAtom);
  if (!user) return null;

  return `${reportStorageUrl}org_storage/${user.primaryOrganisation}/PDF/uploadedPDFs/`;
});

export const selectedGraphTimeRangeAtom = atomWithStorage(
  'selectedGraphTimeRange',
  localValues.selectedGraphTimeRange,
);

/**
 * App-wide preferred language for the AI-explanation texts (reasoning &
 * recommendation), set from the per-finding globe. A backup-language code
 * (e.g. "en") applies to every AI finding on every page; `null` (default)
 * shows each finding in its own original language. Persisted to localStorage.
 */
export const aiExplanationLanguageAtom = atomWithStorage(
  'aiExplanationLanguage',
  localValues.aiExplanationLanguage,
);

export const cookieBannerAcceptedAtom = atomWithStorage(
  'cookieBannerAccepted',
  localValues.cookieBannerAccepted,
);

export const allyPromptBannerDismissedAtom = atomWithStorage(
  'allyPromptBannerDismissed',
  localValues.allyPromptBannerDismissed,
);

// Promotes the glossary feature on the Translate languages page; shown once per browser.
// Bump the key suffix (and atom name) to re-trigger the popup for a future announcement.
export const glossaryNewsSeenAtom = atomWithStorage(
  'glossaryNewsSeenV1',
  localValues.glossaryNewsSeenV1,
);

export const versionOfLastNotificationSeenAtom = atomWithStorage(
  'versionOfLastNotificationSeen',
  localValues.versionOfLastNotificationSeen,
);

export const notificationLastViewDateAtom = atomWithStorage(
  'notificationLastViewDate',
  localValues.notificationLastViewDate,
);

export const userToursSeenAtom = atomWithStorage(
  'userToursSeen',
  localValues.userToursSeen,
);

export const reactTableFiltersAtom = atomWithStorage(
  'reactTableFilters',
  localValues.reactTableFilters,
);

export const accordionExpandedAtom = atomWithStorage(
  'accordionExpanded',
  localValues.accordionExpanded,
);

export const reactTableSortingAtom = atomWithStorage(
  'reactTableSorting',
  localValues.reactTableSorting,
);

export const myGoalsFiltersAtom = atomWithStorage(
  'defaultMyGoalsBoardFilters',
  {
    ...localValues.myGoalsBoardFilters,
    searchText: '',
  },
);

export const spellCheckSitesColumnSortingAtom = atomWithStorage(
  'spellCheckSitesColumnSorting',
  localValues.spellCheckSitesColumnSorting.length
    ? localValues.spellCheckSitesColumnSorting
    : [{ id: 'spellcheckingPageMistakesCount', desc: true }],
);

export const spellCheckSitesColumnFiltersAtom = atomWithStorage(
  'spellCheckSitesColumnFilters',
  localValues.spellCheckSitesColumnFilters,
);
